import React, { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { cn } from "@repo/shared/utils";

export interface AutocompleteOption {
  text: string;
  description?: React.ReactNode;
}

export interface AutocompleteListProps {
  options: AutocompleteOption[];
  onSelect?: (option: AutocompleteOption) => void;
  index: number;
  setIndex: (index: number) => void;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
  onKeyDown?: React.KeyboardEventHandler;
}

export const AutocompleteList = ({
  options,
  onSelect,
  index,
  setIndex,
  onFocus,
  onBlur,
  onKeyDown,
}: AutocompleteListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);

  // scroll current item into view
  useEffect(() => {
    if (!itemRef.current || !scrollRef.current) return;

    const itemBox = itemRef.current.getBoundingClientRect();
    const scrollBox = scrollRef.current.getBoundingClientRect();

    let scrollDelta = 0;

    if (itemBox.bottom > scrollBox.bottom) {
      scrollDelta = itemBox.bottom - scrollBox.bottom;
    }
    if (itemBox.top < scrollBox.top) {
      scrollDelta = itemBox.top - scrollBox.top;
    }

    scrollRef.current.scrollTop += scrollDelta;
  });

  return (
    <div
      ref={scrollRef}
      tabIndex={-1}
      className="overflow-y-auto max-w-[400px] max-h-[300px]"
      onWheel={(evt) => evt.stopPropagation()}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    >
      <div>
        {options.map((option, ind) => (
          <div
            key={ind}
            ref={index === ind ? itemRef : undefined}
            className={cn(
              "px-3 py-1.5 rounded-md cursor-default",
              index === ind && "bg-accent"
            )}
            onMouseMove={() => setIndex(ind)}
            onMouseDown={() => onSelect?.(option)}
          >
            <p>{option.text}</p>
            {!!option.description && (
              <p className="text-[11px] leading-4 text-gray-500">
                {option.description}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export interface AutocompleteProps {
  autocompleteOptions: (string | AutocompleteOption)[];
  onSelect?: (option: string) => void;
  onSelectOption?: (option: AutocompleteOption) => void;
  children: React.ReactNode;
  filterText?: string;
  className?: string;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  onBlur?: () => void;
}

export const Autocomplete: React.FC<AutocompleteProps> = ({
  autocompleteOptions,
  onSelect,
  onSelectOption,
  children,
  filterText = "",
  className,
  open: outerOpen,
  setOpen: outerSetOpen,
  onBlur,
}) => {
  const [innerOpen, innerSetOpen] = useState(false);

  const open = outerSetOpen ? outerOpen : innerOpen;
  const setOpen = outerSetOpen ? outerSetOpen : innerSetOpen;

  const [focusElement, setFocusElement] = useState<Element | null>(null);
  const [index, setIndex] = useState(0);
  const selectionOccurred = useRef(false);
  const blurringTimer = useRef(-1);

  // determine if filter text changed
  const prevFilterText = useRef(filterText);
  const filterChanged = useRef(false);
  if (filterText !== prevFilterText.current) {
    filterChanged.current = true;
  }
  prevFilterText.current = filterText;

  // does item match filter
  const lowerFilterText = filterText.toLowerCase();
  const matches = (option: AutocompleteOption) => {
    if (option.text.toLowerCase().includes(lowerFilterText)) return true;
    return false;
  };
  const filteredOptions = open
    ? autocompleteOptions
        .map((option) =>
          typeof option === "string" ? { text: option } : option
        )
        .filter(matches)
    : [];

  const isFocused = () =>
    focusElement && document.activeElement === focusElement;

  const triggerOpen = () => {
    if (isFocused() && !open) {
      setIndex(0);
      setOpen(true);
    }
  };
  const triggerClose = () => {
    setOpen(false);
  };
  const triggerSelection = (option: AutocompleteOption) => {
    triggerClose();
    if (focusElement && focusElement instanceof HTMLElement) {
      setTimeout(() => focusElement.focus(), 0);
    }

    selectionOccurred.current = true;
    setTimeout(() => (selectionOccurred.current = false), 0);

    onSelect?.(option.text);
    onSelectOption?.(option);
  };
  const optCount = autocompleteOptions.length;

  const keyDown = (evt: React.KeyboardEvent<HTMLDivElement>) => {
    if (!open || !filteredOptions.length) return;

    switch (evt.key) {
      case "Escape":
        triggerClose();
        break;
      case "ArrowUp":
        setIndex((index - 1 + optCount) % optCount);
        break;
      case "ArrowDown":
        setIndex((index + 1) % optCount);
        break;
      case "Enter":
        if (filteredOptions[index]) {
          triggerSelection(filteredOptions[index]);
        }
        break;
      default:
        return;
    }
    evt.preventDefault();
    evt.stopPropagation();
  };

  useEffect(() => {
    if (filterChanged.current) {
      filterChanged.current = false;

      // if filter text changed, and input is focused
      if (!selectionOccurred.current && isFocused()) {
        triggerOpen();
      }
    }
  });

  useEffect(() => setIndex(0), [filterText]);

  return (
    <Popover open={open && !!filteredOptions.length}>
      <PopoverTrigger asChild>
        <div
          className={className}
          onFocus={(evt) => {
            setFocusElement(evt.target);

            if (!selectionOccurred.current && filterText) {
              triggerOpen();
            }
          }}
          onBlurCapture={(evt) => {
            if (selectionOccurred.current) {
              evt.stopPropagation();
            }
          }}
          onBlur={() => {
            blurringTimer.current = window.setTimeout(() => {
              onBlur?.();
              triggerClose();
            }, 100);
          }}
          onKeyDownCapture={keyDown}
          onClick={triggerOpen}
        >
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-auto min-w-[150px]"
        onOpenAutoFocus={(evt) => evt.preventDefault()}
        onWheel={(evt) => evt.stopPropagation()}
        side="bottom"
        align="start"
      >
        <AutocompleteList
          options={filteredOptions}
          onSelect={triggerSelection}
          index={index}
          setIndex={setIndex}
          onFocus={() => clearTimeout(blurringTimer.current)}
          onBlur={() =>
            setTimeout(() => {
              if (!isFocused()) onBlur?.();
              triggerClose();
            })
          }
          onKeyDown={keyDown}
        />
      </PopoverContent>
    </Popover>
  );
};
